import type { AxiosInstance } from 'axios'
import type { IFactoryFunctionRequest } from '@bc-library/interface'
import { EMethod } from '@bc-library/interface'
import { API_MAIN_VERSION, BASE_URL } from '@/config/constantes'
import useAxios from '@/services/api/useAxios'
import type { TSeverity, TTargetCollection } from './alertRuleHttp.service'

const resource = 'triggeredAlerts'

// Types
export type TAlertStatus = 'pending' | 'acknowledged' | 'snoozed' | 'dismissed'

export type TTriggeredAlert = {
  _id: string
  _created: string
  ruleId: string
  entityType: TTargetCollection
  entityId: string
  entityName: string
  entitySnapshot?: Record<string, any>
  triggeredDate: string
  status: TAlertStatus
  snoozedUntil?: string | null
  acknowledgedAt?: string | null
  acknowledgedBy?: string
  severity: TSeverity
  recipients: {
    resourceIds: string[]
    teamIds: string[]
  }
  messageSubject: string
  messageBody: string
  emailSentAt?: string | null
  emailSentTo?: string[]
  notes?: string
  entity?: {
    _id: string
    firstname?: string
    lastname?: string
    picture?: string | null
    display?: string
    projectCode?: string
    resourceId?: {
      _id: string
      firstname?: string
      lastname?: string
      picture?: string | null
    } | null
  } | null
  rule?: {
    _id: string
    name: string
    targetCollection: TTargetCollection
  }
  acknowledger?: {
    _id: string
    firstname: string
    lastname: string
  } | null
}

export type TAlertRecipient = {
  type: 'resource' | 'team' | 'email'
  email: string
  id?: string
  firstname?: string
  lastname?: string
  picture?: string | null
  display?: string
  alreadySent: boolean
}

export type TAlertRecipientsResponse = {
  emailSentAt: string | null
  recipients: TAlertRecipient[]
}

export type TBulkSendEmailResponse = {
  sent: Array<{ _id: string; emails: string[] }>
  skipped: number
}

export type TTriggeredAlertsResponse = {
  docs: TTriggeredAlert[]
  count: {
    total: number
    currentPage: number
    nextPage: number
    totalPages: number
    hasNextPage: boolean
    limit: number
  }
}

export type TAlertStats = {
  byStatus: {
    pending: number
    acknowledged: number
    snoozed: number
    dismissed: number
  }
  bySeverity: {
    low: number
    medium: number
    high: number
    critical: number
  }
  todayTriggered: number
  ruleDaily: Array<{
    date: string
    ruleId: string
    ruleName: string
    entityType: TTargetCollection
    count: number
  }>
  total: number
}

// API Functions
const filter = (axios: IFactoryFunctionRequest<any, any>) => (query: {
  page?: number
  limit?: number
  search?: string
  status?: TAlertStatus | TAlertStatus[]
  severity?: TSeverity | TSeverity[]
  ruleId?: string
  entityType?: TTargetCollection
  targetResourceId?: string
  recipientResourceId?: string
  startDate?: string
  endDate?: string
  sort?: 'date' | 'severity'
}) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2`,
    query,
  })
}

const people = (axios: IFactoryFunctionRequest<any, any>) => (query: { field: 'target' | 'recipient'; search?: string; page?: number; limit?: number }) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/people`,
    query,
  })
}

const getById = (axios: IFactoryFunctionRequest<any, any>) => (id: string) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${id}`,
  })
}

const getStats = (axios: IFactoryFunctionRequest<any, any>) => (query?: {
  entityType?: TTargetCollection
  ruleId?: string
  search?: string
  targetResourceId?: string
  recipientResourceId?: string
  startDate?: string
  endDate?: string
  tz?: string
}) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/stats`,
    query,
  })
}

const acknowledge = (axios: IFactoryFunctionRequest<any, any>) => (id: string, data?: { notes?: string }) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${id}/ack`,
    data: data || {},
  })
}

const snooze = (axios: IFactoryFunctionRequest<any, any>) => (id: string, data: { days: number; notes?: string }) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${id}/snooze`,
    data,
  })
}

const getRecipients = (axios: IFactoryFunctionRequest<any, any>) => (id: string) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${id}/recipients`,
  })
}

const sendEmail = (axios: IFactoryFunctionRequest<any, any>) => (id: string, data?: { emails?: string[] }) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${id}/send-email`,
    data: data || {},
  })
}

const dismiss = (axios: IFactoryFunctionRequest<any, any>) => (id: string, data?: { notes?: string }) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${id}/dismiss`,
    data: data || {},
  })
}

const bulkAcknowledge = (axios: IFactoryFunctionRequest<any, any>) => (alertIds: string[]) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/bulk-ack`,
    data: { alertIds },
  })
}

const bulkDismiss = (axios: IFactoryFunctionRequest<any, any>) => (alertIds: string[]) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/bulk-dismiss`,
    data: { alertIds },
  })
}

const bulkSendEmail = (axios: IFactoryFunctionRequest<any, any>) => (alertIds: string[], onlyNew: boolean) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/bulk-send-email`,
    data: { alertIds, onlyNew },
  })
}

export default (axios: AxiosInstance) => ({
  filter: filter(useAxios(axios)),
  people: people(useAxios(axios)),
  getById: getById(useAxios(axios)),
  getStats: getStats(useAxios(axios)),
  acknowledge: acknowledge(useAxios(axios)),
  snooze: snooze(useAxios(axios)),
  dismiss: dismiss(useAxios(axios)),
  getRecipients: getRecipients(useAxios(axios)),
  sendEmail: sendEmail(useAxios(axios)),
  bulkAcknowledge: bulkAcknowledge(useAxios(axios)),
  bulkDismiss: bulkDismiss(useAxios(axios)),
  bulkSendEmail: bulkSendEmail(useAxios(axios)),
})
